import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, 
  Clock, 
  History, 
  BarChart3, 
  Users, 
  Settings, 
  HelpCircle, 
  LogOut,
  ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Types
interface AppSidebarProps {
  isAdmin?: boolean;
}

interface UserProfile {
  name: string;
  email: string;
  initials: string;
}

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  active: boolean;
  disabled?: boolean;
  badge?: string;
  count?: number;
}

export function AppSidebar({ isAdmin = false }: AppSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Constants
  const USER_DATA: UserProfile = {
    name: 'Mir',
    email: 'mir@octaveinstitute.com',
    initials: 'MI',
  } as const;

  // Check if there's an active session
  const hasActiveSession =
    location.pathname.includes('/app/chat') ||
    location.pathname.includes('/app/voice') ||
    location.pathname.includes('/app/video');

  const mainNavItems: NavigationItem[] = isAdmin ? [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <BarChart3 className="w-5 h-5" />,
      path: '/app/admin',
      active: location.pathname === '/app/admin',
    },
    {
      id: 'users',
      label: 'Users',
      icon: <Users className="w-5 h-5" />,
      path: '/app/admin/users',
      active: location.pathname === '/app/admin/users',
    },
  ] : [
    {
      id: 'home',
      label: 'Home',
      icon: <Home className="w-5 h-5" />,
      path: '/app',
      active: location.pathname === '/app',
    },
    {
      id: 'active',
      label: 'Active Session',
      icon: <Clock className="w-5 h-5" />,
      path: hasActiveSession ? location.pathname : '/app',
      active: hasActiveSession,
      disabled: !hasActiveSession,
      badge: hasActiveSession ? 'Live' : undefined,
    },
    {
      id: 'past',
      label: 'Past Sessions',
      icon: <History className="w-5 h-5" />,
      path: '/app/past-sessions',
      active: location.pathname === '/app/past-sessions',
      count: 6,
    },
  ];

  const secondaryNavItems = [];

  return (
    <aside className="w-64 bg-white border-r border-gray-200 h-full flex flex-col shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <img
            src="/logos/Hupo_Logotype_Orange(noR).svg"
            alt="Hupo"
            className="h-8"
          />
          {isAdmin && (
            <span className="text-xs font-semibold text-orange-700 bg-orange-100 px-2 py-1 rounded-full">
              ADMIN
            </span>
          )}
        </div>
      </div>


      {/* Main Navigation */}
      <nav className="flex-1 p-4">
        <div className="space-y-2">
          {mainNavItems.map((item) => (
            <motion.button
              key={item.id}
              onClick={() => !item.disabled && navigate(item.path)}
              disabled={item.disabled}
              whileHover={{ scale: item.disabled ? 1 : 1.02 }}
              whileTap={{ scale: item.disabled ? 1 : 0.98 }}
              className={`flex items-center w-full px-3 py-3 rounded-lg text-left transition-all duration-200 ${
                item.active
                  ? 'bg-orange-500 text-white shadow-lg'
                  : item.disabled
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <span className={`mr-3 ${item.active ? 'text-white' : ''}`}>
                {item.icon}
              </span>
              <span className="flex-1 font-medium">{item.label}</span>
              
              {item.badge && (
                <span className="ml-2 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                  {item.badge}
                </span>
              )}
              
              {item.count && (
                <span className="ml-2 px-2 py-1 text-xs font-medium bg-gray-200 text-gray-700 rounded-full">
                  {item.count}
                </span>
              )}
            </motion.button>
          ))}
        </div>

        {/* Secondary Navigation */}
        {secondaryNavItems.length > 0 && (
          <div className="mt-8">
            <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              More
            </h3>
            <div className="space-y-2">
              {secondaryNavItems.map((item) => (
                <motion.button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex items-center w-full px-3 py-3 rounded-lg text-left transition-all duration-200 ${
                    item.active
                      ? 'bg-orange-500 text-white shadow-lg'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <span className={`mr-3 ${item.active ? 'text-white' : ''}`}>
                    {item.icon}
                  </span>
                  <span className="flex-1 font-medium">{item.label}</span>
                </motion.button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* User Profile Section */}
      <div className="p-4 border-t border-gray-200 mt-auto">
        <div className="relative">
          <motion.button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center w-full p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold text-sm">
              {USER_DATA.initials}
            </div>
            
            <div className="ml-3 flex-1 text-left">
              <p className="font-semibold text-gray-900">{USER_DATA.name}</p>
              <p className="text-xs text-gray-500">Octave Institute</p>
            </div>
            
            <ChevronDown 
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                userMenuOpen ? 'rotate-180' : ''
              }`} 
            />
          </motion.button>

          {/* User Menu Dropdown */}
          <AnimatePresence>
            {userMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
              >
                <div className="p-2">
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-gray-700 hover:text-gray-900"
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate('/app/settings');
                    }}
                  >
                    <Settings className="w-4 h-4 mr-3" />
                    Settings
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-gray-700 hover:text-gray-900"
                  >
                    <HelpCircle className="w-4 h-4 mr-3" />
                    Help & Support
                  </Button>
                  
                  <div className="border-t border-gray-100 my-1"></div>
                  
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Sign Out
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </aside>
  );
}
